import * as fromActions from '../actions';
import * as fromModels from '../../models';
import { HttpHeaders } from '@angular/common/http';

export interface CreditReportDetailsState {
  summary: SummaryState;
  requestForm: RequestFormState;

  reasons: ReasonsReportState;
  summarySearch: SummarySearchState;
}

export interface RequestFormState {
  loading: boolean;
  loaded: boolean;
  errors: any;
  id: string;
}

export interface ReasonsReportState {
  loading: boolean;
  loaded: boolean;
  errors: any[];
  searchCriteria: fromModels.DownloadReasonsReportRequest;
}

export interface SummarySearchState {
  loading: boolean;
  loaded: boolean;
  errors: any;
  searchCriteria: fromModels.DownloadSummarySearchRequest;
}

export interface SummaryState {
  loading: boolean;
  loaded: boolean;
  errors: any;
  response: fromModels.LoadSummaryResponse;
  searchCriteria: fromModels.LoadSummaryRequest;
  pagingSorting: fromModels.PagingSorting;
}

export const initialState: CreditReportDetailsState = {
  summary: {
    loading: false,
    loaded: false,
    response: null,
    errors: null,
    searchCriteria: {
      creditReportSourceType: null,
      fromDate: null,
      lastName: null,
      locationId: null,
      skip: null,
      socialSecurityNumber: null,
      sort: null,
      take: null,
      toDate: null
    },
    pagingSorting: {
      totalCount: null,
      pageSize: null,
      currentPage: null,
      totalPages: null,
      sortBy: null,
      sortDir: null
    }
  },
  reasons: {
    errors: null,
    loading: false,
    loaded: false,
    searchCriteria: {
      endDate: null,
      startDate: null
    }
  },
  summarySearch: {
    errors: null,
    loading: false,
    loaded: false,
    searchCriteria: {
      creditReportSourceType: null,
      fromDate: null,
      lastName: null,
      locationId: null,
      skip: null,
      socialSecurityNumber: null,
      sort: null,
      take: null,
      toDate: null
    }
  },
  requestForm: {
    loading: false,
    loaded: false,
    errors: null,
    id: null
  }
};

export function reducer(state = initialState, action: fromActions.CreditReportActions): CreditReportDetailsState {
  switch (action.type) {
    case fromActions.CreditReportActionTypes.ProcessQueryString: {
      return {
        ...state,
        summary: {
          ...state.summary,
          loading: false,
          loaded: false,
          errors: null
        }
      };
    }
    case fromActions.CreditReportActionTypes.LoadSummary: {
      console.log('LoadSummary', action.payload);
      return {
        ...state,
        summary: {
          ...state.summary,
          loading: true,
          loaded: false,
          searchCriteria: action.payload
        }
      };
    }

    case fromActions.CreditReportActionTypes.LoadSummarySuccess: {
      return {
        ...state,
        summary: {
          ...updateSearchResultsState(state.summary, action.payload, action.httpHeaders),
          loading: false,
          loaded: true
        }
      };
    }

    case fromActions.CreditReportActionTypes.LoadSummaryFailure: {
      return {
        ...state,
        summary: {
          ...state.summary,
          loading: false,
          loaded: false,
          response: null,
          errors: action.payload
        }
      };
    }

    case fromActions.CreditReportActionTypes.DownloadReasonsReport: {
      return {
        ...state,
        reasons: {
          loading: true,
          loaded: false,
          searchCriteria: action.payload,
          errors: null
        }
      };
    }

    case fromActions.CreditReportActionTypes.DownloadReasonsReportSuccess: {
      return {
        ...state,
        reasons: {
          ...state.reasons,
          loading: false,
          loaded: true
        }
      };
    }

    case fromActions.CreditReportActionTypes.DownloadReasonsReportFailure: {
      return {
        ...state,
        reasons: {
          ...state.reasons,
          loading: false,
          loaded: false,
          errors: action.payload
        }
      };
    }

    case fromActions.CreditReportActionTypes.DownloadSummarySearch: {
      return {
        ...state,
        summarySearch: {
          ...state.summarySearch,
          loading: true,
          loaded: false
        },
        summary: {
          ...state.summary,
          loading: true,
          loaded: false
        }
      };
    }

    case fromActions.CreditReportActionTypes.DownloadSummarySearchSuccess: {
      return {
        ...state,
        summarySearch: {
          ...state.summarySearch,
          loading: false,
          loaded: true
        },
        summary: {
          ...state.summary,
          loading: false,
          loaded: true
        }
      };
    }

    case fromActions.CreditReportActionTypes.DownloadSummarySearchFailure: {
      return {
        ...state,
        summarySearch: {
          ...state.summarySearch,
          loading: false,
          loaded: false
        },
        summary: {
          ...state.summary,
          loading: false,
          loaded: false
        }
      };
    }

    case fromActions.CreditReportActionTypes.DownloadRequestForm: {
      return {
        ...state,
        requestForm: {
          loading: true,
          loaded: false,
          id: null,
          errors: null
        }
      };
    }

    case fromActions.CreditReportActionTypes.DownloadRequestFormSuccess: {
      return {
        ...state,
        requestForm: {
          ...state.requestForm,
          loading: false,
          loaded: true
        }
      };
    }

    case fromActions.CreditReportActionTypes.DownloadRequestFormFailure: {
      return {
        ...state,
        requestForm: {
          ...state.requestForm,
          loading: false,
          loaded: false,
          errors: action.payload.error
        }
      };
    }

    case fromActions.CreditReportActionTypes.ExecuteSearch:
      return {
        ...state,
        summary: {
          ...state.summary,
          loading: true,
          loaded: false,
          searchCriteria: action.payload
        },
        summarySearch: {
          ...state.summarySearch,
          searchCriteria: action.payload
        }
      };

    case fromActions.CreditReportActionTypes.SortBy:
    case fromActions.CreditReportActionTypes.NextPage:
    case fromActions.CreditReportActionTypes.PrevPage:
    case fromActions.CreditReportActionTypes.FirstPage:
    case fromActions.CreditReportActionTypes.LastPage:
    case fromActions.CreditReportActionTypes.SetPageSize:
      return state;

    default: {
      if (Object.values(fromActions.CreditReportActionTypes).includes((action as fromActions.CreditReportActions).type)) {
        console.log('credit-report.reducer: unhandled action: ', action);
      }
    }
  }

  return state;
}

function updateSearchResultsState(state: SummaryState, response: fromModels.LoadSummaryResponse, headers: HttpHeaders) {
  let pagingSorting: fromModels.PagingSorting;

  // Grab special header we use for paging
  const pagingHeader: string = headers.get('X-Pagination');
  const pagingObject: object = JSON.parse(pagingHeader);

  // Populate paging information from http headers
  pagingSorting = {
    totalCount: pagingObject['totalCount'],
    pageSize: pagingObject['pageSize'],
    currentPage: pagingObject['currentPage'],
    totalPages: pagingObject['totalPages'],
    sortBy: pagingObject['sortBy'],
    sortDir: pagingObject['sortDir']
  };

  return {
    ...state,
    response,
    pagingSorting
  };
}

export const getCreditReportSummary = (state: CreditReportDetailsState) => state.summary;
export const getCreditReportReasons = (state: CreditReportDetailsState) => state.reasons;
export const getCreditReportSummarySearch = (state: CreditReportDetailsState) => state.summarySearch;
export const getCreditReportRequestForm = (state: CreditReportDetailsState) => state.requestForm;
